<ng-container *transloco="let t">
    <div class="guidelines-dialog dialog-container sai-dialog-shell">
        <app-sai-dialog-header
            iconLigature="upload_file"
            [title]="t('Data Upload Guidelines')"
            [subtitle]="t('Follow these guidelines to ensure successful data upload and processing')"
            [showClose]="false"
        ></app-sai-dialog-header>

        <div class="content">
            <div class="guidelines-container">
                <div class="guideline-section critical">
                    <div class="section-header">
                        <mat-icon class="section-icon">warning</mat-icon>
                        <h2 class="section-title">
                            {{ t("Critical Requirements") }}
                        </h2>
                        <span class="section-badge">{{
                            t("Must Follow")
                        }}</span>
                    </div>
                    <div class="guidelines-grid">
                        <div class="guideline-card">
                            <div class="card-icon">
                                <mat-icon>schedule</mat-icon>
                            </div>
                            <div class="card-content">
                                <h3 class="card-title">
                                    {{ t("Date Formatting") }}
                                </h3>
                                <p class="card-description">
                                    {{ t("Use consistent date formats:") }}
                                    <span class="code-example">DD-MM-YYYY</span>
                                    {{ t("or") }}
                                    <span class="code-example">DD/MM/YYYY</span>
                                </p>
                            </div>
                        </div>

                        <div class="guideline-card">
                            <div class="card-icon">
                                <mat-icon>numbers</mat-icon>
                            </div>
                            <div class="card-content">
                                <h3 class="card-title">
                                    {{ t("Numeric Data") }}
                                </h3>
                                <p class="card-description">
                                    {{
                                        t(
                                            "Remove symbols and units from numeric cells. Use"
                                        )
                                    }}
                                    <span class="code-example">10</span>
                                    {{ t("instead of") }}
                                    <span class="code-example">10g</span>
                                </p>
                            </div>
                        </div>

                        <div class="guideline-card">
                            <div class="card-icon">
                                <mat-icon>storage</mat-icon>
                            </div>
                            <div class="card-content">
                                <h3 class="card-title">
                                    {{ t("File Size Limit") }}
                                </h3>
                                <p class="card-description">
                                    {{ t("Maximum file size:") }}
                                    <span class="highlight">200MB</span>
                                </p>
                            </div>
                        </div>

                        <div class="guideline-card">
                            <div class="card-icon">
                                <mat-icon>description</mat-icon>
                            </div>
                            <div class="card-content">
                                <h3 class="card-title">
                                    {{ t("File Format") }}
                                </h3>
                                <p class="card-description">
                                    {{ t("Supported formats:") }}
                                    <span class="code-example">CSV</span>,
                                    <span class="code-example"
                                        >Excel (.xlsx, .xls)</span
                                    >
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Data Quality Section -->
                <div class="guideline-section quality">
                    <div class="section-header">
                        <mat-icon class="section-icon">check_circle</mat-icon>
                        <h2 class="section-title">{{ t("Data Quality") }}</h2>
                        <span class="section-badge">{{
                            t("Best Practices")
                        }}</span>
                    </div>
                    <div class="guidelines-grid">
                        <div class="guideline-card">
                            <div class="card-icon">
                                <mat-icon>label</mat-icon>
                            </div>
                            <div class="card-content">
                                <h3 class="card-title">
                                    {{ t("Column Naming") }}
                                </h3>
                                <p class="card-description">
                                    {{ t("Use clear, descriptive names like") }}
                                    <span class="code-example">{{
                                        t("Date of Purchase")
                                    }}</span>
                                </p>
                            </div>
                        </div>

                        <div class="guideline-card">
                            <div class="card-icon">
                                <mat-icon>block</mat-icon>
                            </div>
                            <div class="card-content">
                                <h3 class="card-title">
                                    {{ t("Avoid Special Characters") }}
                                </h3>
                                <p class="card-description">
                                    {{ t("No symbols like") }}
                                    <span class="code-example">!</span>,
                                    <span class="code-example">&#64;</span>,
                                    <span class="code-example">#</span>
                                </p>
                            </div>
                        </div>

                        <div class="guideline-card">
                            <div class="card-icon">
                                <mat-icon>data_object</mat-icon>
                            </div>
                            <div class="card-content">
                                <h3 class="card-title">
                                    {{ t("Data Type Consistency") }}
                                </h3>
                                <p class="card-description">
                                    {{
                                        t(
                                            "Keep each column to one data type only"
                                        )
                                    }}
                                </p>
                            </div>
                        </div>

                        <div class="guideline-card">
                            <div class="card-icon">
                                <mat-icon>content_copy</mat-icon>
                            </div>
                            <div class="card-content">
                                <h3 class="card-title">
                                    {{ t("Remove Duplicates") }}
                                </h3>
                                <p class="card-description">
                                    {{
                                        t(
                                            "Clean duplicate records before upload"
                                        )
                                    }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Technical Requirements Section -->
                <div class="guideline-section technical">
                    <div class="section-header">
                        <mat-icon class="section-icon">settings</mat-icon>
                        <h2 class="section-title">
                            {{ t("Technical Requirements") }}
                        </h2>
                        <span class="section-badge">{{ t("Technical") }}</span>
                    </div>
                    <div class="guidelines-grid">
                        <div class="guideline-card">
                            <div class="card-icon">
                                <mat-icon>code</mat-icon>
                            </div>
                            <div class="card-content">
                                <h3 class="card-title">{{ t("Encoding") }}</h3>
                                <p class="card-description">
                                    {{ t("Use") }}
                                    <span class="code-example">UTF-8</span>
                                    {{ t("encoding") }}
                                </p>
                            </div>
                        </div>

                        <div class="guideline-card">
                            <div class="card-icon">
                                <mat-icon>space_bar</mat-icon>
                            </div>
                            <div class="card-content">
                                <h3 class="card-title">
                                    {{ t("Missing Data") }}
                                </h3>
                                <p class="card-description">
                                    {{
                                        t("Leave cells blank instead of using")
                                    }}
                                    <span class="code-example">N/A</span>
                                    {{ t("or") }}
                                    <span class="code-example">-</span>
                                </p>
                            </div>
                        </div>

                        <div class="guideline-card">
                            <div class="card-icon">
                                <mat-icon>security</mat-icon>
                            </div>
                            <div class="card-content">
                                <h3 class="card-title">
                                    {{ t("Data Privacy") }}
                                </h3>
                                <p class="card-description">
                                    {{
                                        t(
                                            "Avoid sensitive personal information"
                                        )
                                    }}
                                </p>
                            </div>
                        </div>

                        <div class="guideline-card">
                            <div class="card-icon">
                                <mat-icon>verified</mat-icon>
                            </div>
                            <div class="card-content">
                                <h3 class="card-title">
                                    {{ t("Review & Validate") }}
                                </h3>
                                <p class="card-description">
                                    {{
                                        t("Always review data before uploading")
                                    }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="buttons">
            <div class="button-group">
                <button
                    type="button"
                    class="cancel-btn"
                    mat-flat-button
                    (click)="cancel()"
                >
                    <mat-icon>close</mat-icon>
                    <span>{{ t("Cancel") }}</span>
                </button>
                <button
                    type="button"
                    class="confirm-btn bg-primary"
                    mat-flat-button
                    (click)="save()"
                >
                    <mat-icon>check</mat-icon>
                    <span>{{ t("I Understand & Continue") }}</span>
                </button>
            </div>
        </div>
    </div>
</ng-container>
